import { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom/client";
import html2canvas from "html2canvas";

/* ══════════════════════════════════════════════════
   FACEBOOK ADS — 5 Square Animated Creatives (15s+)
   Flow: Audit scan (title → desc → highlights) → Google Shopping #1
   ══════════════════════════════════════════════════ */

/* ── Shared UI atoms ── */

function GoogleLogo({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48">
      <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.77-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.9 7.35 2.56 10.56l7.97-5.97z"/>
      <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 5.97C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

function Brand({ light, size = 11 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <svg width={size * 1.4} height={size * 1.4} viewBox="0 0 48 48" fill="none">
        <path d="M18 4L20.5 14L31 16.5L20.5 19L18 29L15.5 19L5 16.5L15.5 14Z" fill={light ? "#fff" : "#4B6AE8"}/>
        <path d="M34 6L35.2 10L39 11.2L35.2 12.4L34 16L32.8 12.4L29 11.2L32.8 10Z" fill={light ? "rgba(255,255,255,0.7)" : "#4B6AE8"} opacity="0.7"/>
      </svg>
      <span style={{ fontSize: size, fontWeight: 700, color: light ? "#fff" : "#0f172a", fontFamily: "'Inter',sans-serif" }}>
        Magic<span style={{ fontWeight: 400 }}>Feed</span>
        <span style={{ color: light ? "#c7d2fe" : "#4B6AE8", fontSize: size * 0.78, fontWeight: 700, marginLeft: 1 }}>Pro</span>
      </span>
    </div>
  );
}

function ImagePlaceholder({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#dadce0">
      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
    </svg>
  );
}

/* Ghost product card */
function GhostCard({ style = {} }) {
  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e8eaed", opacity: 0.4, overflow: "hidden", display: "flex", flexDirection: "column", ...style }}>
      <div style={{ flex: 1, minHeight: 0, background: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ImagePlaceholder size={22}/>
      </div>
      <div style={{ padding: "6px 8px" }}>
        <div style={{ fontSize: 9, color: "#70757a", fontWeight: 500 }}>Product</div>
        <div style={{ width: "80%", height: 3, background: "#e8eaed", borderRadius: 2, marginTop: 4 }}/>
        <div style={{ width: "50%", height: 3, background: "#e8eaed", borderRadius: 2, marginTop: 3 }}/>
      </div>
    </div>
  );
}

/* Animated product card — starts ghost, then reveals */
function HeroProductCard({ revealed, ranked, style = {} }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 10,
      border: revealed ? "2px solid #4B6AE8" : "1px solid #e8eaed",
      boxShadow: ranked ? "0 4px 20px rgba(75,106,232,0.2)" : "none",
      overflow: "hidden", display: "flex", flexDirection: "column",
      transition: "all 0.6s ease", ...style,
    }}>
      <div style={{
        flex: 1, minHeight: 0, position: "relative",
        background: revealed ? "linear-gradient(135deg,#eef2ff,#dde5ff)" : "#f8f9fa",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "background 0.6s ease",
      }}>
        {revealed ? <span style={{ fontSize: 28 }}>🎒</span> : <ImagePlaceholder size={22}/>}
        {ranked && (
          <div style={{
            position: "absolute", top: 5, left: 5,
            background: "linear-gradient(135deg,#4B6AE8,#818cf8)", color: "#fff",
            fontSize: 8, fontWeight: 800, padding: "2px 7px", borderRadius: 10,
            animation: "popIn 0.4s ease",
          }}>#1</div>
        )}
      </div>
      <div style={{ padding: "6px 8px" }}>
        {revealed ? (
          <>
            <span style={{ fontSize: 7, fontWeight: 600, color: "#4B6AE8", background: "rgba(75,106,232,0.08)", padding: "1px 5px", borderRadius: 8 }}>✦ AI Optimized</span>
            <div style={{ fontSize: 8, fontWeight: 600, color: "#0f172a", marginTop: 3, lineHeight: 1.3 }}>Premium & Durable Luxury Laptop Backpack — 15.6"</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>$89.99</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 9, color: "#70757a", fontWeight: 500 }}>Product</div>
            <div style={{ width: "80%", height: 3, background: "#e8eaed", borderRadius: 2, marginTop: 4 }}/>
            <div style={{ width: "50%", height: 3, background: "#e8eaed", borderRadius: 2, marginTop: 3 }}/>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Google Shopping Results Panel ── */
function ShoppingPanel({ revealed, ranked, badge }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e8eaed", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
      {/* Google header */}
      <div style={{ padding: "10px 14px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <GoogleLogo size={18}/>
          <span style={{ fontSize: 13, fontWeight: 500, color: "#202124" }}>Shopping</span>
        </div>
        {/* Search bar */}
        <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #dfe1e5", borderRadius: 24, padding: "7px 12px", marginBottom: 6, boxShadow: "0 1px 4px rgba(32,33,36,0.08)" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="#9AA0A6"><path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
          <span style={{ marginLeft: 6, fontSize: 11, color: "#202124" }}>Luxury Laptop Backpack</span>
          <span style={{ marginLeft: "auto", fontSize: 13, color: "#70757a" }}>×</span>
        </div>
        {/* Tabs */}
        <div style={{ display: "flex", gap: 14, paddingBottom: 6, borderBottom: "1px solid #ebebeb" }}>
          <span style={{ fontSize: 10, color: "#5f6368" }}>All</span>
          <span style={{ fontSize: 10, color: "#1a73e8", fontWeight: 600, borderBottom: "2px solid #1a73e8", paddingBottom: 4 }}>Shopping</span>
          <span style={{ fontSize: 10, color: "#5f6368" }}>Images</span>
          <span style={{ fontSize: 10, color: "#5f6368" }}>News</span>
        </div>
      </div>
      {/* Sponsored + badge */}
      <div style={{ padding: "8px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: "#202124" }}>Sponsored products</span>
        {badge && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, background: "linear-gradient(135deg,#ecfdf5,#d1fae5)", border: "1px solid #6ee7b7", borderRadius: 14, padding: "3px 10px", animation: "popIn 0.5s ease" }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#10b981" }}/>
            <span style={{ fontSize: 8, fontWeight: 700, color: "#059669" }}>Your product is now #1 🎉</span>
          </div>
        )}
      </div>
      {/* Product cards */}
      <div style={{ padding: "0 14px 14px", display: "flex", gap: 8 }}>
        <HeroProductCard revealed={revealed} ranked={ranked} style={{ flex: "1.2 1 0", minWidth: 0 }}/>
        <GhostCard style={{ flex: "1 1 0", minWidth: 0 }}/>
        <GhostCard style={{ flex: "1 1 0", minWidth: 0 }}/>
        <GhostCard style={{ flex: "1 1 0", minWidth: 0 }}/>
      </div>
    </div>
  );
}

/* ── Audit check row ── */
function AuditRow({ icon, label, status, detail, active }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
      background: active ? (status === "pass" ? "#f0fdf4" : status === "fail" ? "#fef2f2" : "#fff") : "#fafafa",
      borderRadius: 8, border: `1px solid ${active ? (status === "pass" ? "#bbf7d0" : status === "fail" ? "#fecaca" : "#e5e7eb") : "#f1f1f1"}`,
      transition: "all 0.5s ease",
      opacity: active ? 1 : 0.3,
    }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: "#0f172a" }}>{label}</div>
        {active && detail && <div style={{ fontSize: 8, color: status === "pass" ? "#059669" : "#dc2626", fontWeight: 600, marginTop: 1 }}>{detail}</div>}
      </div>
      {active && (
        <div style={{
          width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
          background: status === "pass" ? "#10B981" : status === "fail" ? "#EF4444" : "#F59E0B",
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "popIn 0.3s ease",
        }}>
          <span style={{ color: "#fff", fontSize: 8, fontWeight: 800 }}>{status === "pass" ? "✓" : status === "fail" ? "!" : "~"}</span>
        </div>
      )}
      {!active && (
        <div style={{ width: 16, height: 16, borderRadius: "50%", border: "1.5px solid #e5e7eb", flexShrink: 0 }}/>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 1: Classic Audit → Shopping (vertical split)
   ══════════════════════════════════════════════════ */
function Variant1() {
  const [step, setStep] = useState(0);
  // Steps: 0=init, 1=scanning, 2=title check, 3=desc check, 4=highlights check, 5=score reveal, 6=shopping appear, 7=product reveal, 8=rank #1, 9=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); await w(800);
        if (!running) return;
        setStep(1); await w(1200);
        setStep(2); await w(1500);
        setStep(3); await w(1500);
        setStep(4); await w(1500);
        setStep(5); await w(1800);
        setStep(6); await w(1200);
        setStep(7); await w(1200);
        setStep(8); await w(1000);
        setStep(9); await w(4000);
        if (!running) return;
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #f8faff 0%, #eef2ff 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#94a3b8" }}>Free AI Feed Audit</div>
      </div>

      {/* Audit panel */}
      <div style={{ padding: "0 16px", flex: step < 6 ? 1 : 0, transition: "flex 0.8s ease", overflow: "hidden" }}>
        <div style={{ background: "#fff", borderRadius: 12, padding: "12px", border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <div style={{ width: 20, height: 20, borderRadius: 6, background: "linear-gradient(135deg, #4B6AE8, #818cf8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 10, color: "#fff" }}>✦</span>
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>Feed Audit</span>
            {step >= 1 && step < 5 && (
              <div style={{ marginLeft: "auto", width: 12, height: 12, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
            )}
            {step >= 5 && (
              <div style={{ marginLeft: "auto", fontSize: 9, fontWeight: 700, color: "#EF4444", animation: "popIn 0.4s ease" }}>Score: 32/100</div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <AuditRow icon="📝" label="Title Quality" active={step >= 2} status={step >= 2 ? "fail" : null} detail='Only 12 chars — "Laptop bag"'/>
            <AuditRow icon="📄" label="Description SEO" active={step >= 3} status={step >= 3 ? "fail" : null} detail="120 chars — needs 500+ for ranking"/>
            <AuditRow icon="⭐" label="Product Highlights" active={step >= 4} status={step >= 4 ? "fail" : null} detail="0/5 highlights — Google requires 5"/>
          </div>
        </div>
      </div>

      {/* Shopping results panel */}
      <div style={{
        padding: "8px 16px 12px", flex: step >= 6 ? 1 : 0,
        opacity: step >= 6 ? 1 : 0, transform: step >= 6 ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.8s ease", overflow: "hidden",
      }}>
        <ShoppingPanel revealed={step >= 7} ranked={step >= 8} badge={step >= 9}/>
      </div>

      {/* Bottom CTA */}
      <div style={{ padding: "0 16px 12px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(75,106,232,0.35)" }}>
          Audit My Feed — Free
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 2: Dark Mode — Score Counter + Shopping
   ══════════════════════════════════════════════════ */
function Variant2() {
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);
  // Steps: 0=init, 1=scan title, 2=scan desc, 3=scan highlights, 4=score counting, 5=score done, 6=transition, 7=shopping, 8=reveal, 9=rank, 10=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setScore(0); await w(600);
        setStep(1); await w(1500);
        setStep(2); await w(1500);
        setStep(3); await w(1500);
        setStep(4);
        for (let i = 0; i <= 32; i++) { if (!running) return; setScore(i); await w(40); }
        setStep(5); await w(1800);
        setStep(6); await w(800);
        setStep(7); await w(800);
        setStep(8); await w(1000);
        setStep(9); await w(1000);
        setStep(10); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  const scoreColor = score < 40 ? "#EF4444" : score < 70 ? "#F59E0B" : "#10B981";

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
      <div style={{ position: "absolute", width: "50%", height: "50%", borderRadius: "50%", background: "radial-gradient(circle, rgba(75,106,232,0.1), transparent)", top: "-10%", right: "-10%", pointerEvents: "none" }}/>

      {/* Header */}
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 1 }}>
        <Brand light/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#64748b" }}>AI Feed Audit</div>
      </div>

      {/* Score + Audit checks */}
      <div style={{ flex: step < 6 ? 1 : 0, padding: "0 16px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, transition: "flex 0.8s ease", overflow: "hidden", zIndex: 1 }}>
        {/* Gauge */}
        <div style={{ position: "relative" }}>
          <svg width={90} height={90} viewBox="0 0 90 90">
            <circle cx={45} cy={45} r={38} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={5}/>
            <circle cx={45} cy={45} r={38} fill="none" stroke={scoreColor} strokeWidth={5}
              strokeDasharray={`${(score / 100) * 238.76} 238.76`} strokeLinecap="round"
              style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%", transition: "stroke-dasharray 0.05s linear, stroke 0.3s ease" }}/>
            <text x={45} y={43} textAnchor="middle" dominantBaseline="central" fill={scoreColor} fontSize={22} fontWeight="900" fontFamily="Inter,sans-serif">{score}</text>
            <text x={45} y={58} textAnchor="middle" fill="#64748b" fontSize={8} fontFamily="Inter,sans-serif">/100</text>
          </svg>
        </div>
        {/* Check items */}
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 4 }}>
          {[
            { icon: "📝", label: "Title", done: step >= 1, detail: "12 chars — FAIL" },
            { icon: "📄", label: "Description", done: step >= 2, detail: "120 chars — FAIL" },
            { icon: "⭐", label: "Highlights", done: step >= 3, detail: "0/5 — FAIL" },
          ].map((c, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "5px 10px",
              background: c.done ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.03)",
              borderRadius: 6, border: `1px solid ${c.done ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.06)"}`,
              opacity: c.done ? 1 : 0.3, transition: "all 0.5s ease",
            }}>
              <span style={{ fontSize: 12 }}>{c.icon}</span>
              <span style={{ fontSize: 9, fontWeight: 600, color: "#e2e8f0", flex: 1 }}>{c.label}</span>
              {c.done && <span style={{ fontSize: 8, color: "#EF4444", fontWeight: 700 }}>{c.detail}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Shopping results */}
      <div style={{
        flex: step >= 6 ? 1 : 0, padding: step >= 6 ? "8px 12px" : "0 12px",
        opacity: step >= 6 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden", zIndex: 1,
      }}>
        <ShoppingPanel revealed={step >= 8} ranked={step >= 9} badge={step >= 10}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "0 16px 12px", textAlign: "center", zIndex: 1 }}>
        <div style={{ display: "inline-block", background: "#fff", color: "#4B6AE8", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(0,0,0,0.2)" }}>
          → Get My Score
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 3: Side-by-side — Before audit / After audit
   ══════════════════════════════════════════════════ */
function Variant3() {
  const [step, setStep] = useState(0);
  // 0=init, 1=show before card, 2=scan title, 3=scan desc, 4=scan highlights, 5=show score, 6=arrow, 7=show after, 8=shopping, 9=reveal, 10=rank, 11=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); await w(600);
        setStep(1); await w(1200);
        setStep(2); await w(1200);
        setStep(3); await w(1200);
        setStep(4); await w(1200);
        setStep(5); await w(1500);
        setStep(6); await w(800);
        setStep(7); await w(800);
        setStep(8); await w(1000);
        setStep(9); await w(1000);
        setStep(10); await w(1000);
        setStep(11); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "#fff", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#4B6AE8", textTransform: "uppercase", letterSpacing: 1 }}>Feed Audit</div>
      </div>

      {/* BEFORE side */}
      <div style={{
        flex: step < 6 ? 1 : 0, background: "linear-gradient(135deg, #fef2f2, #fee2e2)",
        margin: "0 12px", borderRadius: 12, padding: step < 6 ? "12px" : "0 12px",
        overflow: "hidden", transition: "all 0.8s ease",
        opacity: step >= 1 ? 1 : 0.3,
      }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: "#EF4444", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Before Audit</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <AuditRow icon="📝" label="Title" active={step >= 2} status="fail" detail='"Laptop bag" — 12 chars'/>
          <AuditRow icon="📄" label="Description" active={step >= 3} status="fail" detail="120 chars only"/>
          <AuditRow icon="⭐" label="Highlights" active={step >= 4} status="fail" detail="0/5 missing"/>
        </div>
        {step >= 5 && (
          <div style={{ marginTop: 8, textAlign: "center", animation: "popIn 0.4s ease" }}>
            <span style={{ fontSize: 24, fontWeight: 900, color: "#EF4444" }}>32</span>
            <span style={{ fontSize: 10, color: "#991b1b" }}>/100</span>
          </div>
        )}
      </div>

      {/* Arrow transition */}
      {step >= 6 && (
        <div style={{ textAlign: "center", padding: "4px 0", animation: "popIn 0.4s ease" }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, #4B6AE8, #818cf8)", boxShadow: "0 2px 8px rgba(75,106,232,0.3)" }}>
            <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, transform: "rotate(90deg)", display: "inline-block" }}>→</span>
          </div>
        </div>
      )}

      {/* AFTER side with Shopping */}
      <div style={{
        flex: step >= 7 ? 1 : 0, background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
        margin: "0 12px", borderRadius: 12, padding: step >= 7 ? "8px" : "0 8px",
        overflow: "hidden", transition: "all 0.8s ease",
        opacity: step >= 7 ? 1 : 0,
      }}>
        {step >= 7 && (
          <>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#10B981", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>After AI Optimization</div>
            <ShoppingPanel revealed={step >= 9} ranked={step >= 10} badge={step >= 11}/>
          </>
        )}
      </div>

      {/* CTA */}
      <div style={{ padding: "8px 16px 12px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(75,106,232,0.35)" }}>
          Start Free Audit
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 4: Terminal/Console style — hacker aesthetic
   ══════════════════════════════════════════════════ */
function Variant4() {
  const [lines, setLines] = useState([]);
  const [shoppingStep, setShoppingStep] = useState(0);
  // 0=hidden, 1=show, 2=reveal product, 3=rank, 4=badge

  useEffect(() => {
    let running = true;
    const allLines = [
      { text: "$ magicfeedpro audit --url store.myshopify.com", color: "#10B981", delay: 600 },
      { text: "Connecting to Merchant Center...", color: "#64748b", delay: 800 },
      { text: "Scanning 1,247 products...", color: "#64748b", delay: 1000 },
      { text: "", color: "", delay: 300 },
      { text: "📝 Title Quality ........... FAIL (12 chars avg)", color: "#EF4444", delay: 1200 },
      { text: "📄 Description SEO ......... FAIL (120 chars avg)", color: "#EF4444", delay: 1200 },
      { text: "⭐ Product Highlights ...... FAIL (0/5 missing)", color: "#EF4444", delay: 1200 },
      { text: "", color: "", delay: 300 },
      { text: "Feed Score: 32/100 — CRITICAL", color: "#EF4444", delay: 1500 },
      { text: "", color: "", delay: 300 },
      { text: "$ magicfeedpro fix --ai", color: "#10B981", delay: 800 },
      { text: "Optimizing titles with AI... ✓", color: "#10B981", delay: 800 },
      { text: "Enriching descriptions... ✓", color: "#10B981", delay: 800 },
      { text: "Generating 5/5 highlights... ✓", color: "#10B981", delay: 800 },
      { text: "New Score: 98/100 ✦", color: "#4B6AE8", delay: 1200 },
    ];

    const loop = async () => {
      while (running) {
        setLines([]); setShoppingStep(0);
        for (let i = 0; i < allLines.length; i++) {
          if (!running) return;
          setLines(prev => [...prev, allLines[i]]);
          await new Promise(r => setTimeout(r, allLines[i].delay));
        }
        if (!running) return;
        await new Promise(r => setTimeout(r, 600));
        setShoppingStep(1);
        await new Promise(r => setTimeout(r, 600));
        setShoppingStep(2);
        await new Promise(r => setTimeout(r, 800));
        setShoppingStep(3);
        await new Promise(r => setTimeout(r, 600));
        setShoppingStep(4);
        await new Promise(r => setTimeout(r, 4000));
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "#0c0c0c", fontFamily: "'Courier New', monospace", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Terminal header */}
      <div style={{ padding: "8px 12px", background: "#1a1a1a", display: "flex", alignItems: "center", gap: 6, borderBottom: "1px solid #333" }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444" }}/>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#F59E0B" }}/>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981" }}/>
        <span style={{ marginLeft: 8, fontSize: 9, color: "#666" }}>MagicFeedPro — Feed Audit</span>
      </div>

      {/* Terminal output */}
      <div style={{ flex: shoppingStep === 0 ? 1 : 0, padding: "8px 12px", overflow: "hidden", transition: "flex 0.6s ease" }}>
        {lines.map((l, i) => (
          <div key={i} style={{ fontSize: 9, color: l.color || "#666", lineHeight: 1.6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {l.text || "\u00A0"}
          </div>
        ))}
        {lines.length > 0 && shoppingStep === 0 && (
          <span style={{ display: "inline-block", width: 6, height: 12, background: "#10B981", animation: "pulseMF 0.8s infinite", marginTop: 2 }}/>
        )}
      </div>

      {/* Shopping results */}
      {shoppingStep >= 1 && (
        <div style={{ flex: 1, padding: "6px 10px 10px", transition: "flex 0.6s ease", overflow: "hidden" }}>
          <ShoppingPanel revealed={shoppingStep >= 2} ranked={shoppingStep >= 3} badge={shoppingStep >= 4}/>
        </div>
      )}

      {/* Brand */}
      <div style={{ padding: "4px 12px 8px" }}><Brand light size={9}/></div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 5: Score journey — big number counter + progress bars
   ══════════════════════════════════════════════════ */
function Variant5() {
  const [step, setStep] = useState(0);
  const [titlePct, setTitlePct] = useState(0);
  const [descPct, setDescPct] = useState(0);
  const [hlPct, setHlPct] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  // 0=init, 1=title scanning, 2=title done, 3=desc scanning, 4=desc done, 5=hl scanning, 6=hl done, 7=total, 8=fixing, 9=fixed, 10=shopping, 11=reveal, 12=rank, 13=badge
  useEffect(() => {
    let running = true;
    const animatePct = (setter, target, speed = 20) => new Promise(resolve => {
      let val = 0;
      const tick = () => {
        if (!running) { resolve(); return; }
        val = Math.min(val + 2, target);
        setter(val);
        if (val < target) setTimeout(tick, speed);
        else resolve();
      };
      tick();
    });
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setTitlePct(0); setDescPct(0); setHlPct(0); setTotalScore(0);
        await w(600);
        setStep(1); await animatePct(setTitlePct, 18); setStep(2); await w(800);
        setStep(3); await animatePct(setDescPct, 8); setStep(4); await w(800);
        setStep(5); await animatePct(setHlPct, 0, 50); setStep(6); await w(800);
        setStep(7);
        for (let i = 0; i <= 32; i++) { if (!running) return; setTotalScore(i); await w(30); }
        await w(1500);
        setStep(8); await w(800);
        setStep(9); await w(800);
        setStep(10); await w(800);
        setStep(11); await w(1000);
        setStep(12); await w(800);
        setStep(13); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  const scoreColor = totalScore < 40 ? "#EF4444" : "#10B981";

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #fff 0%, #f8faff 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px 4px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        {step >= 1 && step < 8 && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 10, height: 10, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
            <span style={{ fontSize: 8, color: "#4B6AE8", fontWeight: 600 }}>Auditing...</span>
          </div>
        )}
      </div>

      {/* Score + progress bars */}
      <div style={{ flex: step < 10 ? 1 : 0, padding: "0 16px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "flex 0.8s ease" }}>
        {/* Big score */}
        <div style={{ fontSize: 52, fontWeight: 900, color: scoreColor, lineHeight: 1, letterSpacing: -3, transition: "color 0.5s ease" }}>
          {totalScore}
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, marginBottom: 16 }}>Feed Score /100</div>

        {/* Progress bars */}
        <div style={{ width: "100%", maxWidth: 280, display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { label: "📝 Title Quality", pct: titlePct, max: 35, active: step >= 1 },
            { label: "📄 Description SEO", pct: descPct, max: 30, active: step >= 3 },
            { label: "⭐ Highlights", pct: hlPct, max: 20, active: step >= 5 },
          ].map((bar, i) => (
            <div key={i} style={{ opacity: bar.active ? 1 : 0.2, transition: "opacity 0.5s ease" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ fontSize: 9, fontWeight: 600, color: "#374151" }}>{bar.label}</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: bar.pct < bar.max * 0.5 ? "#EF4444" : "#10B981" }}>{bar.pct}/{bar.max}</span>
              </div>
              <div style={{ width: "100%", height: 6, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ width: `${(bar.pct / bar.max) * 100}%`, height: "100%", background: bar.pct < bar.max * 0.5 ? "#EF4444" : "#10B981", borderRadius: 4, transition: "width 0.1s linear" }}/>
              </div>
            </div>
          ))}
        </div>

        {step >= 8 && step < 10 && (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg, #ecfdf5, #d1fae5)", borderRadius: 8, padding: "6px 14px", animation: "popIn 0.4s ease" }}>
            <span style={{ fontSize: 10 }}>✦</span>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#059669" }}>AI fixing all issues...</span>
          </div>
        )}
      </div>

      {/* Shopping results */}
      <div style={{
        flex: step >= 10 ? 1 : 0, padding: step >= 10 ? "4px 12px" : "0 12px",
        opacity: step >= 10 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden",
      }}>
        <ShoppingPanel revealed={step >= 11} ranked={step >= 12} badge={step >= 13}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "4px 16px 12px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(75,106,232,0.35)" }}>
          Audit My Feed — Free
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 6: Money Leak — Revenue draining animation
   ══════════════════════════════════════════════════ */
function Variant6() {
  const [step, setStep] = useState(0);
  const [lost, setLost] = useState(0);
  // 0=init, 1=leak starts, 2=leak counting, 3=why title, 4=why desc, 5=why hl, 6=stop leak, 7=fixing, 8=fixed, 9=shopping, 10=reveal, 11=rank, 12=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setLost(0); await w(600);
        setStep(1); await w(400);
        setStep(2);
        for (let i = 0; i <= 47; i++) { if (!running) return; setLost(i * 89); await w(50); }
        await w(600);
        setStep(3); await w(1200);
        setStep(4); await w(1200);
        setStep(5); await w(1200);
        setStep(6); await w(1000);
        setStep(7); await w(1200);
        setStep(8); await w(800);
        setStep(9); await w(800);
        setStep(10); await w(800);
        setStep(11); await w(800);
        setStep(12); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #0f172a 0%, #1e1b2e 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
      {/* Header */}
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 1 }}>
        <Brand light/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#64748b" }}>Revenue Leak Detector</div>
      </div>

      {/* Money leak section */}
      <div style={{ flex: step < 9 ? 1 : 0, padding: "0 16px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "flex 0.8s ease", zIndex: 1 }}>
        {step >= 1 && step < 8 && (
          <>
            <div style={{ fontSize: 9, color: "#ef4444", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6, animation: step >= 2 ? "pulseMF 1.5s infinite" : "none" }}>
              💸 Revenue Leaking
            </div>
            <div style={{ fontSize: 42, fontWeight: 900, color: "#EF4444", lineHeight: 1, letterSpacing: -2 }}>
              -${lost.toLocaleString()}
            </div>
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4, marginBottom: 16 }}>Lost this month from poor feed quality</div>
          </>
        )}
        {step >= 8 && step < 9 && (
          <>
            <div style={{ fontSize: 9, color: "#10b981", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6, animation: "popIn 0.5s ease" }}>
              ✦ Leak Stopped
            </div>
            <div style={{ fontSize: 42, fontWeight: 900, color: "#10B981", lineHeight: 1, letterSpacing: -2, animation: "popIn 0.5s ease" }}>
              +$4,183
            </div>
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>Recovered revenue after AI optimization</div>
          </>
        )}

        {/* Reasons */}
        {step >= 3 && step < 8 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 4, marginTop: 12 }}>
            {[
              { s: 3, icon: "📝", text: "Weak titles = 73% lower CTR" },
              { s: 4, icon: "📄", text: "Short descriptions = missing keywords" },
              { s: 5, icon: "⭐", text: "No highlights = disapproved products" },
            ].map((r, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "5px 10px",
                background: step >= r.s ? "rgba(239,68,68,0.1)" : "transparent",
                borderRadius: 6, border: `1px solid ${step >= r.s ? "rgba(239,68,68,0.2)" : "transparent"}`,
                opacity: step >= r.s ? 1 : 0, transition: "all 0.5s ease",
              }}>
                <span style={{ fontSize: 12 }}>{r.icon}</span>
                <span style={{ fontSize: 9, color: "#fca5a5", fontWeight: 600 }}>{r.text}</span>
              </div>
            ))}
          </div>
        )}

        {step >= 6 && step < 8 && (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6, background: "rgba(75,106,232,0.15)", borderRadius: 8, padding: "6px 14px", animation: "popIn 0.4s ease" }}>
            <div style={{ width: 10, height: 10, border: "2px solid #818cf8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#818cf8" }}>AI fixing your feed...</span>
          </div>
        )}
      </div>

      {/* Shopping results */}
      <div style={{ flex: step >= 9 ? 1 : 0, padding: step >= 9 ? "8px 12px" : "0 12px", opacity: step >= 9 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden", zIndex: 1 }}>
        <ShoppingPanel revealed={step >= 10} ranked={step >= 11} badge={step >= 12}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "0 16px 12px", textAlign: "center", zIndex: 1 }}>
        <div style={{ display: "inline-block", background: step >= 8 ? "linear-gradient(135deg, #10B981, #34d399)" : "linear-gradient(135deg, #EF4444, #f87171)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, transition: "background 0.5s ease" }}>
          {step >= 8 ? "Get My Results — Free" : "Stop The Leak — Free Audit"}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 7: Invisible Products — Your products can't be found
   ══════════════════════════════════════════════════ */
function Variant7() {
  const [step, setStep] = useState(0);
  // 0=init, 1=search, 2=no results, 3=why title, 4=why desc, 5=why hl, 6=fixing, 7=found!, 8=shopping, 9=reveal, 10=rank, 11=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); await w(800);
        setStep(1); await w(1500);
        setStep(2); await w(2000);
        setStep(3); await w(1200);
        setStep(4); await w(1200);
        setStep(5); await w(1200);
        setStep(6); await w(1500);
        setStep(7); await w(1200);
        setStep(8); await w(800);
        setStep(9); await w(800);
        setStep(10); await w(800);
        setStep(11); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "#fff", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#EF4444", textTransform: "uppercase" }}>Feed Alert</div>
      </div>

      {/* Google search mockup */}
      <div style={{ padding: "0 16px 8px" }}>
        <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #dfe1e5", borderRadius: 24, padding: "7px 12px", boxShadow: "0 1px 4px rgba(32,33,36,0.08)" }}>
          <GoogleLogo size={14}/>
          <span style={{ marginLeft: 6, fontSize: 11, color: "#202124" }}>{step >= 1 ? "Your product name" : ""}</span>
          {step >= 1 && step < 2 && <div style={{ marginLeft: 4, width: 1, height: 14, background: "#4285f4", animation: "pulseMF 0.5s infinite" }}/>}
        </div>
      </div>

      {/* Content area */}
      <div style={{ flex: 1, padding: "0 16px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* No results state */}
        {step >= 2 && step < 7 && (
          <div style={{ textAlign: "center", padding: "16px 0", animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 36, marginBottom: 8, opacity: 0.3 }}>👻</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>Your products are invisible</div>
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>Google can't find them. Here's why:</div>
          </div>
        )}

        {/* Reasons */}
        {step >= 3 && step < 7 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
            <AuditRow icon="📝" label="Title too generic" active={step >= 3} status="fail" detail='"Laptop bag" won&apos;t rank'/>
            <AuditRow icon="📄" label="Description too short" active={step >= 4} status="fail" detail="Google needs 500+ chars"/>
            <AuditRow icon="⭐" label="Missing highlights" active={step >= 5} status="fail" detail="0/5 — required by Google"/>
          </div>
        )}

        {/* Fixing */}
        {step >= 6 && step < 7 && (
          <div style={{ textAlign: "center", marginTop: 12, animation: "popIn 0.4s ease" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg, #eef2ff, #dde5ff)", borderRadius: 8, padding: "8px 16px" }}>
              <div style={{ width: 12, height: 12, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#4B6AE8" }}>AI optimizing your feed...</span>
            </div>
          </div>
        )}

        {/* Found! */}
        {step >= 7 && step < 8 && (
          <div style={{ textAlign: "center", padding: "12px 0", animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#10B981" }}>Now they can see you!</div>
            <div style={{ fontSize: 10, color: "#059669", marginTop: 4 }}>Score: 32 → 98/100</div>
          </div>
        )}

        {/* Shopping panel */}
        {step >= 8 && (
          <div style={{ flex: 1, overflow: "hidden", animation: "popIn 0.5s ease" }}>
            <ShoppingPanel revealed={step >= 9} ranked={step >= 10} badge={step >= 11}/>
          </div>
        )}
      </div>

      {/* CTA */}
      <div style={{ padding: "8px 16px 12px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(75,106,232,0.35)" }}>
          {step < 7 ? "Make My Products Visible" : "Audit My Feed — Free"}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 8: Competitor Spy — They outrank you
   ══════════════════════════════════════════════════ */
function Variant8() {
  const [step, setStep] = useState(0);
  // 0=init, 1=show competitor, 2=show you (low), 3=gap highlight, 4=scan title, 5=scan desc, 6=scan hl, 7=fixing, 8=overtaking, 9=shopping, 10=reveal, 11=rank, 12=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); await w(600);
        setStep(1); await w(1200);
        setStep(2); await w(1500);
        setStep(3); await w(1500);
        setStep(4); await w(1000);
        setStep(5); await w(1000);
        setStep(6); await w(1000);
        setStep(7); await w(1500);
        setStep(8); await w(1500);
        setStep(9); await w(800);
        setStep(10); await w(800);
        setStep(11); await w(800);
        setStep(12); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #f8faff 0%, #eef2ff 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#EF4444", textTransform: "uppercase", letterSpacing: 1 }}>Competitor Alert</div>
      </div>

      {/* Comparison section */}
      <div style={{ flex: step < 9 ? 1 : 0, padding: "0 14px", overflow: "hidden", transition: "flex 0.8s ease" }}>
        {/* VS header */}
        {step >= 1 && step < 9 && (
          <div style={{ textAlign: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>Your Feed vs Competitors</div>
          </div>
        )}

        {/* Bars comparison */}
        {step >= 1 && step < 9 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { label: "Title Quality", you: 18, them: 92, active: step >= 4 },
              { label: "Description SEO", you: 8, them: 87, active: step >= 5 },
              { label: "Highlights", you: 0, them: 95, active: step >= 6 },
            ].map((b, i) => (
              <div key={i} style={{ opacity: b.active ? 1 : (step >= 1 ? 0.3 : 0), transition: "opacity 0.5s ease" }}>
                <div style={{ fontSize: 9, fontWeight: 600, color: "#374151", marginBottom: 4 }}>{b.label}</div>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                      <span style={{ fontSize: 7, color: "#94a3b8" }}>You</span>
                      <span style={{ fontSize: 7, fontWeight: 700, color: step >= 8 ? "#10B981" : "#EF4444" }}>{step >= 8 ? "96" : b.you}</span>
                    </div>
                    <div style={{ width: "100%", height: 5, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: `${step >= 8 ? 96 : b.you}%`, height: "100%", background: step >= 8 ? "#10B981" : "#EF4444", borderRadius: 3, transition: "all 0.8s ease" }}/>
                    </div>
                  </div>
                  <span style={{ fontSize: 8, fontWeight: 800, color: "#94a3b8" }}>vs</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                      <span style={{ fontSize: 7, color: "#94a3b8" }}>Top competitor</span>
                      <span style={{ fontSize: 7, fontWeight: 700, color: "#10B981" }}>{b.them}</span>
                    </div>
                    <div style={{ width: "100%", height: 5, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: `${b.them}%`, height: "100%", background: "#10B981", borderRadius: 3 }}/>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Gap alert */}
        {step >= 3 && step < 7 && (
          <div style={{ marginTop: 10, textAlign: "center", animation: "popIn 0.4s ease" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "6px 14px" }}>
              <span style={{ fontSize: 10 }}>⚠️</span>
              <span style={{ fontSize: 9, fontWeight: 700, color: "#dc2626" }}>You're losing to competitors</span>
            </div>
          </div>
        )}

        {step >= 7 && step < 9 && (
          <div style={{ marginTop: 10, textAlign: "center", animation: "popIn 0.4s ease" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: 8, padding: "6px 14px" }}>
              <span style={{ fontSize: 10 }}>✦</span>
              <span style={{ fontSize: 9, fontWeight: 700, color: "#059669" }}>{step >= 8 ? "You're now beating them! 🎉" : "AI optimizing your feed..."}</span>
            </div>
          </div>
        )}
      </div>

      {/* Shopping results */}
      <div style={{ flex: step >= 9 ? 1 : 0, padding: step >= 9 ? "4px 12px" : "0 12px", opacity: step >= 9 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden" }}>
        <ShoppingPanel revealed={step >= 10} ranked={step >= 11} badge={step >= 12}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "6px 16px 12px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(75,106,232,0.35)" }}>
          Beat My Competitors — Free Audit
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 9: Merchant Center Disapprovals
   ══════════════════════════════════════════════════ */
function Variant9() {
  const [step, setStep] = useState(0);
  const [disapproved, setDisapproved] = useState(0);
  // 0=init, 1=alert, 2=counting disapprovals, 3=reason1, 4=reason2, 5=reason3, 6=fixing, 7=approved!, 8=shopping, 9=reveal, 10=rank, 11=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setDisapproved(0); await w(600);
        setStep(1); await w(1000);
        setStep(2);
        for (let i = 0; i <= 847; i += 17) { if (!running) return; setDisapproved(Math.min(i, 847)); await w(30); }
        await w(800);
        setStep(3); await w(1200);
        setStep(4); await w(1200);
        setStep(5); await w(1200);
        setStep(6); await w(1800);
        setStep(7); await w(1500);
        setStep(8); await w(800);
        setStep(9); await w(800);
        setStep(10); await w(800);
        setStep(11); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "#fff", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header — mimics Merchant Center */}
      <div style={{ padding: "10px 14px", background: "#f8f9fa", borderBottom: "1px solid #dadce0", display: "flex", alignItems: "center", gap: 8 }}>
        <GoogleLogo size={16}/>
        <span style={{ fontSize: 11, fontWeight: 500, color: "#5f6368" }}>Merchant Center</span>
        <span style={{ marginLeft: "auto" }}><Brand size={8}/></span>
      </div>

      {/* Content */}
      <div style={{ flex: step < 8 ? 1 : 0, padding: "10px 14px", overflow: "hidden", transition: "flex 0.8s ease" }}>
        {/* Alert banner */}
        {step >= 1 && (
          <div style={{ background: step >= 7 ? "#e6f4ea" : "#fce8e6", borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, marginBottom: 10, transition: "background 0.5s ease" }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: step >= 7 ? "#34a853" : "#ea4335", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 800 }}>{step >= 7 ? "✓" : "!"}</span>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: step >= 7 ? "#137333" : "#c5221f" }}>
                {step >= 7 ? "All products approved ✓" : `${disapproved} products disapproved`}
              </div>
              <div style={{ fontSize: 8, color: step >= 7 ? "#137333" : "#a50e0e", marginTop: 1 }}>
                {step >= 7 ? "Feed score: 98/100 — all issues fixed" : "Your products won't appear in Shopping"}
              </div>
            </div>
          </div>
        )}

        {/* Disapproval reasons */}
        {step >= 3 && step < 7 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#202124", marginBottom: 4 }}>Disapproval reasons:</div>
            {[
              { s: 3, text: "Insufficient product title", count: "412 products" },
              { s: 4, text: "Missing product description", count: "298 products" },
              { s: 5, text: "No product highlights", count: "847 products" },
            ].map((r, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
                background: "#fef2f2", borderRadius: 6, borderLeft: "3px solid #ea4335",
                opacity: step >= r.s ? 1 : 0, transition: "opacity 0.5s ease",
              }}>
                <span style={{ fontSize: 9, color: "#c5221f", fontWeight: 600, flex: 1 }}>{r.text}</span>
                <span style={{ fontSize: 8, color: "#a50e0e", fontWeight: 700, background: "#fce8e6", padding: "2px 6px", borderRadius: 4 }}>{r.count}</span>
              </div>
            ))}
          </div>
        )}

        {/* Fixing */}
        {step >= 6 && step < 7 && (
          <div style={{ marginTop: 12, textAlign: "center", animation: "popIn 0.4s ease" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#e8eaf6", borderRadius: 8, padding: "6px 14px" }}>
              <div style={{ width: 10, height: 10, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
              <span style={{ fontSize: 9, fontWeight: 700, color: "#4B6AE8" }}>AI fixing all issues...</span>
            </div>
          </div>
        )}
      </div>

      {/* Shopping results */}
      <div style={{ flex: step >= 8 ? 1 : 0, padding: step >= 8 ? "4px 12px" : "0 12px", opacity: step >= 8 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden" }}>
        <ShoppingPanel revealed={step >= 9} ranked={step >= 10} badge={step >= 11}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "6px 16px 12px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(75,106,232,0.35)" }}>
          Fix My Disapprovals — Free
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 10: Wasted Ad Spend — Budget burning
   ══════════════════════════════════════════════════ */
function Variant10() {
  const [step, setStep] = useState(0);
  const [waste, setWaste] = useState(0);
  // 0=init, 1=ad spend counting, 2=waste reveal, 3=scan title, 4=scan desc, 5=scan hl, 6=fix, 7=optimized, 8=shopping, 9=reveal, 10=rank, 11=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setWaste(0); await w(600);
        setStep(1);
        for (let i = 0; i <= 67; i++) { if (!running) return; setWaste(i); await w(40); }
        await w(800);
        setStep(2); await w(1500);
        setStep(3); await w(1000);
        setStep(4); await w(1000);
        setStep(5); await w(1000);
        setStep(6); await w(1500);
        setStep(7); await w(1200);
        setStep(8); await w(800);
        setStep(9); await w(800);
        setStep(10); await w(800);
        setStep(11); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#d97706", textTransform: "uppercase" }}>Ad Spend Alert</div>
      </div>

      {/* Content */}
      <div style={{ flex: step < 8 ? 1 : 0, padding: "0 16px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "flex 0.8s ease" }}>
        {step >= 1 && step < 7 && (
          <>
            <div style={{ fontSize: 9, color: "#92400e", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>
              🔥 Your Google Ads budget
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span style={{ fontSize: 38, fontWeight: 900, color: "#d97706", lineHeight: 1 }}>{waste}%</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>wasted</span>
            </div>
            <div style={{ fontSize: 10, color: "#92400e", marginTop: 4, marginBottom: 14 }}>Due to poor feed quality</div>
          </>
        )}

        {step >= 7 && step < 8 && (
          <div style={{ textAlign: "center", animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 9, color: "#059669", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>✦ Feed Optimized</div>
            <div style={{ fontSize: 38, fontWeight: 900, color: "#10B981", lineHeight: 1 }}>3.2x</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#059669", marginTop: 4 }}>Better ROAS</div>
          </div>
        )}

        {/* Audit reasons */}
        {step >= 2 && step < 7 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#92400e", marginBottom: 2 }}>Why your ads underperform:</div>
            {[
              { s: 3, icon: "📝", label: "Weak titles", detail: "Low click-through rate" },
              { s: 4, icon: "📄", label: "Thin descriptions", detail: "Missing search keywords" },
              { s: 5, icon: "⭐", label: "No highlights", detail: "Products get rejected" },
            ].map((r, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "5px 10px",
                background: step >= r.s ? "#fff" : "#fef9ee",
                borderRadius: 6, border: `1px solid ${step >= r.s ? "#f59e0b" : "#fde68a"}`,
                opacity: step >= r.s ? 1 : 0.3, transition: "all 0.5s ease",
              }}>
                <span style={{ fontSize: 12 }}>{r.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#0f172a" }}>{r.label}</div>
                  {step >= r.s && <div style={{ fontSize: 8, color: "#d97706", fontWeight: 600 }}>{r.detail}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {step >= 6 && step < 7 && (
          <div style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", borderRadius: 8, padding: "6px 14px", animation: "popIn 0.4s ease", border: "1px solid #e5e7eb" }}>
            <div style={{ width: 10, height: 10, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#4B6AE8" }}>AI optimizing feed...</span>
          </div>
        )}
      </div>

      {/* Shopping results */}
      <div style={{ flex: step >= 8 ? 1 : 0, padding: step >= 8 ? "4px 12px" : "0 12px", opacity: step >= 8 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden" }}>
        <ShoppingPanel revealed={step >= 9} ranked={step >= 10} badge={step >= 11}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "6px 16px 12px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #d97706, #f59e0b)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(217,119,6,0.35)" }}>
          Stop Wasting — Free Audit
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 11: Time Bomb — Every day = lost sales
   ══════════════════════════════════════════════════ */
function Variant11() {
  const [step, setStep] = useState(0);
  const [days, setDays] = useState(0);
  const [salesLost, setSalesLost] = useState(0);
  // 0=init, 1=clock ticking, 2=days counting, 3=scan, 4=title, 5=desc, 6=hl, 7=fix, 8=saved, 9=shopping, 10=reveal, 11=rank, 12=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setDays(0); setSalesLost(0); await w(600);
        setStep(1); await w(400);
        setStep(2);
        for (let i = 1; i <= 30; i++) { if (!running) return; setDays(i); setSalesLost(i * 47); await w(60); }
        await w(1000);
        setStep(3); await w(600);
        setStep(4); await w(1000);
        setStep(5); await w(1000);
        setStep(6); await w(1000);
        setStep(7); await w(1500);
        setStep(8); await w(1200);
        setStep(9); await w(800);
        setStep(10); await w(800);
        setStep(11); await w(800);
        setStep(12); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: step >= 8 ? "linear-gradient(180deg, #ecfdf5 0%, #d1fae5 100%)" : "linear-gradient(180deg, #fef2f2 0%, #fee2e2 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden", transition: "background 0.8s ease" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 700, color: step >= 8 ? "#059669" : "#EF4444", textTransform: "uppercase", transition: "color 0.5s ease" }}>
          {step >= 8 ? "Feed Fixed ✓" : "⏰ Time Alert"}
        </div>
      </div>

      {/* Counter */}
      <div style={{ flex: step < 9 ? 1 : 0, padding: "0 16px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "flex 0.8s ease" }}>
        {step >= 1 && step < 8 && (
          <>
            <div style={{ fontSize: 48, fontWeight: 900, color: "#EF4444", lineHeight: 1, letterSpacing: -2 }}>{days}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#dc2626", marginTop: 2 }}>days with a broken feed</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "#991b1b", marginTop: 8, opacity: 0.6 }}>= ${salesLost.toLocaleString()} lost</div>

            {step >= 4 && (
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 3, marginTop: 14 }}>
                <AuditRow icon="📝" label="Title" active={step >= 4} status="fail" detail="Missing keywords"/>
                <AuditRow icon="📄" label="Description" active={step >= 5} status="fail" detail="Too short for SEO"/>
                <AuditRow icon="⭐" label="Highlights" active={step >= 6} status="fail" detail="0/5 — disapproved"/>
              </div>
            )}
          </>
        )}

        {step >= 8 && step < 9 && (
          <div style={{ textAlign: "center", animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 48, fontWeight: 900, color: "#10B981", lineHeight: 1 }}>0</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#059669", marginTop: 2 }}>issues remaining</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#065f46", marginTop: 8 }}>Feed Score: 98/100 ✦</div>
          </div>
        )}

        {step >= 7 && step < 8 && (
          <div style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", borderRadius: 8, padding: "6px 14px", animation: "popIn 0.4s ease", border: "1px solid #e5e7eb" }}>
            <div style={{ width: 10, height: 10, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#4B6AE8" }}>AI fixing feed now...</span>
          </div>
        )}
      </div>

      {/* Shopping */}
      <div style={{ flex: step >= 9 ? 1 : 0, padding: step >= 9 ? "4px 12px" : "0 12px", opacity: step >= 9 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden" }}>
        <ShoppingPanel revealed={step >= 10} ranked={step >= 11} badge={step >= 12}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "6px 16px 12px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #EF4444, #f87171)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(239,68,68,0.35)" }}>
          Fix It Now — Free Audit
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 12: X-Ray Scanner — Deep product analysis
   ══════════════════════════════════════════════════ */
function Variant12() {
  const [step, setStep] = useState(0);
  const [scanLine, setScanLine] = useState(0);
  // 0=init, 1=product appears, 2=xray scanning, 3=title found, 4=desc found, 5=hl found, 6=diagnosis, 7=treating, 8=cured, 9=shopping, 10=reveal, 11=rank, 12=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setScanLine(0); await w(600);
        setStep(1); await w(1000);
        setStep(2);
        for (let i = 0; i <= 100; i += 2) { if (!running) return; setScanLine(i); await w(30); }
        setStep(3); await w(1000);
        setStep(4); await w(1000);
        setStep(5); await w(1000);
        setStep(6); await w(1500);
        setStep(7); await w(1500);
        setStep(8); await w(1200);
        setStep(9); await w(800);
        setStep(10); await w(800);
        setStep(11); await w(800);
        setStep(12); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #0f172a 0%, #020617 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
      {/* Grid overlay */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(75,106,232,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(75,106,232,0.03) 1px, transparent 1px)", backgroundSize: "20px 20px", pointerEvents: "none" }}/>

      {/* Header */}
      <div style={{ padding: "12px 16px 6px", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 1 }}>
        <Brand light/>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#4B6AE8", textTransform: "uppercase", letterSpacing: 1 }}>X-Ray Mode</div>
      </div>

      {/* Product being scanned */}
      <div style={{ flex: step < 9 ? 1 : 0, padding: "0 16px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "flex 0.8s ease", zIndex: 1 }}>
        {step >= 1 && (
          <div style={{ width: "100%", maxWidth: 280, position: "relative" }}>
            {/* Product card outline */}
            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(75,106,232,0.2)", borderRadius: 10, padding: 12, position: "relative", overflow: "hidden" }}>
              {/* Scan line */}
              {step === 2 && (
                <div style={{ position: "absolute", left: 0, right: 0, top: `${scanLine}%`, height: 2, background: "linear-gradient(90deg, transparent, #4B6AE8, transparent)", boxShadow: "0 0 10px #4B6AE8", zIndex: 2 }}/>
              )}
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ width: 50, height: 50, borderRadius: 8, background: "rgba(75,106,232,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>🎒</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#e2e8f0" }}>Laptop Backpack</div>
                  <div style={{ fontSize: 8, color: "#64748b", marginTop: 2 }}>$89.99 · store.myshopify.com</div>
                </div>
              </div>

              {/* Scan results */}
              {step >= 3 && step < 8 && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                  {[
                    { s: 3, label: "Title", value: "12 chars", color: "#EF4444", icon: "✗" },
                    { s: 4, label: "Description", value: "120 chars", color: "#EF4444", icon: "✗" },
                    { s: 5, label: "Highlights", value: "0/5", color: "#EF4444", icon: "✗" },
                  ].map((r, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", gap: 6, padding: "4px 8px",
                      background: step >= r.s ? "rgba(239,68,68,0.06)" : "transparent",
                      borderRadius: 4, opacity: step >= r.s ? 1 : 0, transition: "all 0.5s ease",
                    }}>
                      <span style={{ fontSize: 9, color: r.color, fontWeight: 800, width: 12 }}>{r.icon}</span>
                      <span style={{ fontSize: 9, color: "#94a3b8", flex: 1 }}>{r.label}</span>
                      <span style={{ fontSize: 8, color: r.color, fontWeight: 700 }}>{r.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {step >= 8 && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                  {[
                    { label: "Title", value: "62 chars ✓", color: "#10B981" },
                    { label: "Description", value: "4,970 chars ✓", color: "#10B981" },
                    { label: "Highlights", value: "5/5 ✓", color: "#10B981" },
                  ].map((r, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", background: "rgba(16,185,129,0.06)", borderRadius: 4, animation: "popIn 0.3s ease" }}>
                      <span style={{ fontSize: 9, color: r.color, fontWeight: 800, width: 12 }}>✓</span>
                      <span style={{ fontSize: 9, color: "#94a3b8", flex: 1 }}>{r.label}</span>
                      <span style={{ fontSize: 8, color: r.color, fontWeight: 700 }}>{r.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Diagnosis */}
            {step >= 6 && step < 8 && (
              <div style={{ marginTop: 8, textAlign: "center" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: step >= 7 ? "rgba(75,106,232,0.15)" : "rgba(239,68,68,0.15)", borderRadius: 8, padding: "6px 14px", animation: "popIn 0.4s ease" }}>
                  {step >= 7 ? (
                    <div style={{ width: 10, height: 10, border: "2px solid #818cf8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
                  ) : (
                    <span style={{ fontSize: 10 }}>⚠️</span>
                  )}
                  <span style={{ fontSize: 9, fontWeight: 700, color: step >= 7 ? "#818cf8" : "#f87171" }}>
                    {step >= 7 ? "AI rewriting feed..." : "Critical: Feed score 32/100"}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Shopping */}
      <div style={{ flex: step >= 9 ? 1 : 0, padding: step >= 9 ? "4px 12px" : "0 12px", opacity: step >= 9 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden", zIndex: 1 }}>
        <ShoppingPanel revealed={step >= 10} ranked={step >= 11} badge={step >= 12}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "6px 16px 12px", textAlign: "center", zIndex: 1 }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #818cf8)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(75,106,232,0.4)" }}>
          X-Ray My Feed — Free
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 13: Social Proof — Merchants who fixed their feed
   ══════════════════════════════════════════════════ */
function Variant13() {
  const [step, setStep] = useState(0);
  const [counter, setCounter] = useState(0);
  // 0=init, 1=counter, 2=testimonial1, 3=testimonial2, 4=your turn, 5=scan, 6=title, 7=desc, 8=hl, 9=shopping, 10=reveal, 11=rank, 12=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setCounter(0); await w(600);
        setStep(1);
        for (let i = 0; i <= 2847; i += 57) { if (!running) return; setCounter(Math.min(i, 2847)); await w(20); }
        await w(1000);
        setStep(2); await w(2000);
        setStep(3); await w(2000);
        setStep(4); await w(1200);
        setStep(5); await w(600);
        setStep(6); await w(800);
        setStep(7); await w(800);
        setStep(8); await w(800);
        setStep(9); await w(800);
        setStep(10); await w(800);
        setStep(11); await w(800);
        setStep(12); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 50%, #f0f9ff 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
          {[1,2,3,4,5].map(s => <span key={s} style={{ fontSize: 9, color: "#f59e0b" }}>★</span>)}
          <span style={{ fontSize: 8, color: "#92400e", fontWeight: 600, marginLeft: 2 }}>4.9</span>
        </div>
      </div>

      {/* Counter */}
      <div style={{ flex: step < 9 ? 1 : 0, padding: "0 16px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "flex 0.8s ease" }}>
        {step >= 1 && step < 4 && (
          <>
            <div style={{ fontSize: 42, fontWeight: 900, color: "#059669", lineHeight: 1 }}>{counter.toLocaleString()}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#065f46", marginTop: 4 }}>Shopify merchants fixed their feed</div>
          </>
        )}

        {/* Testimonials */}
        {step >= 2 && step < 4 && (
          <div style={{ width: "100%", marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              { s: 2, name: "Sarah K.", store: "Fashion Store", stat: "+67% impressions", text: "My products were invisible. Now they rank #1!" },
              { s: 3, name: "Mike R.", store: "Tech Gadgets", stat: "+43% clicks", text: "Feed score went from 28 to 96 in minutes." },
            ].map((t, i) => (
              <div key={i} style={{
                background: "#fff", borderRadius: 10, padding: "8px 12px", border: "1px solid #d1fae5",
                opacity: step >= t.s ? 1 : 0, transform: step >= t.s ? "translateY(0)" : "translateY(10px)",
                transition: "all 0.5s ease",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: "linear-gradient(135deg, #4B6AE8, #818cf8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 9, color: "#fff", fontWeight: 700 }}>{t.name[0]}</span>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#0f172a" }}>{t.name}</div>
                    <div style={{ fontSize: 7, color: "#94a3b8" }}>{t.store}</div>
                  </div>
                  <span style={{ marginLeft: "auto", fontSize: 8, fontWeight: 800, color: "#10B981", background: "#ecfdf5", padding: "2px 6px", borderRadius: 4 }}>{t.stat}</span>
                </div>
                <div style={{ fontSize: 8, color: "#374151", fontStyle: "italic", lineHeight: 1.4 }}>"{t.text}"</div>
              </div>
            ))}
          </div>
        )}

        {/* Your turn */}
        {step >= 4 && step < 9 && (
          <div style={{ textAlign: "center", animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>Your turn?</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <AuditRow icon="📝" label="Title" active={step >= 6} status={step >= 6 ? "fail" : null} detail="Needs optimization"/>
              <AuditRow icon="📄" label="Description" active={step >= 7} status={step >= 7 ? "fail" : null} detail="Too short"/>
              <AuditRow icon="⭐" label="Highlights" active={step >= 8} status={step >= 8 ? "fail" : null} detail="Missing"/>
            </div>
          </div>
        )}
      </div>

      {/* Shopping */}
      <div style={{ flex: step >= 9 ? 1 : 0, padding: step >= 9 ? "4px 12px" : "0 12px", opacity: step >= 9 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden" }}>
        <ShoppingPanel revealed={step >= 10} ranked={step >= 11} badge={step >= 12}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "6px 16px 12px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #059669, #10B981)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(5,150,105,0.35)" }}>
          Join 2,847 Merchants — Free Audit
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 14: Phone Notification — Push alert style
   ══════════════════════════════════════════════════ */
function Variant14() {
  const [step, setStep] = useState(0);
  // 0=init, 1=notif1, 2=notif2, 3=notif3, 4=alert, 5=scan, 6=title, 7=desc, 8=hl, 9=fix, 10=shopping, 11=reveal, 12=rank, 13=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); await w(800);
        setStep(1); await w(1200);
        setStep(2); await w(1200);
        setStep(3); await w(1200);
        setStep(4); await w(1500);
        setStep(5); await w(600);
        setStep(6); await w(1000);
        setStep(7); await w(1000);
        setStep(8); await w(1000);
        setStep(9); await w(1500);
        setStep(10); await w(800);
        setStep(11); await w(800);
        setStep(12); await w(800);
        setStep(13); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  const notifs = [
    { s: 1, icon: "📉", title: "Impressions dropped -34%", body: "Your products lost visibility this week", time: "2h ago", color: "#EF4444" },
    { s: 2, icon: "⚠️", title: "12 products disapproved", body: "Google Merchant Center rejected items", time: "5h ago", color: "#F59E0B" },
    { s: 3, icon: "😱", title: "Competitor outranking you", body: "tech-store.com took your #1 position", time: "1d ago", color: "#EF4444" },
  ];

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #1e1e2e 0%, #0f0f1a 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Phone status bar */}
      <div style={{ padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: "#fff" }}>9:41</span>
        <div style={{ display: "flex", gap: 4 }}>
          <div style={{ width: 14, height: 8, borderRadius: 2, border: "1px solid #fff", position: "relative" }}>
            <div style={{ position: "absolute", inset: 1, background: "#10B981", borderRadius: 1 }}/>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div style={{ flex: step < 10 ? 1 : 0, padding: "4px 12px", display: "flex", flexDirection: "column", gap: 6, overflow: "hidden", transition: "flex 0.8s ease" }}>
        {notifs.filter(n => step >= n.s).map((n, i) => (
          <div key={i} style={{
            background: "rgba(255,255,255,0.08)", backdropFilter: "blur(20px)",
            borderRadius: 14, padding: "10px 12px", display: "flex", gap: 8,
            animation: "popIn 0.5s ease", border: "1px solid rgba(255,255,255,0.06)",
          }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: `${n.color}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 14 }}>{n.icon}</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "#fff" }}>{n.title}</span>
                <span style={{ fontSize: 7, color: "#64748b" }}>{n.time}</span>
              </div>
              <div style={{ fontSize: 8, color: "#94a3b8", marginTop: 2 }}>{n.body}</div>
            </div>
          </div>
        ))}

        {/* Main alert */}
        {step >= 4 && step < 10 && (
          <div style={{ background: "rgba(75,106,232,0.12)", borderRadius: 14, padding: 12, border: "1px solid rgba(75,106,232,0.2)", animation: "popIn 0.5s ease", marginTop: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <Brand light size={9}/>
              <span style={{ fontSize: 8, color: "#818cf8", fontWeight: 600 }}>Feed Audit Result</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {[
                { s: 6, icon: "📝", label: "Title", fail: true },
                { s: 7, icon: "📄", label: "Description", fail: true },
                { s: 8, icon: "⭐", label: "Highlights", fail: true },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, opacity: step >= r.s ? 1 : 0.3, transition: "opacity 0.5s ease" }}>
                  <span style={{ fontSize: 10 }}>{r.icon}</span>
                  <span style={{ fontSize: 8, color: "#e2e8f0", flex: 1 }}>{r.label}</span>
                  {step >= r.s && <span style={{ fontSize: 8, color: "#EF4444", fontWeight: 700 }}>FAIL</span>}
                </div>
              ))}
            </div>
            {step >= 9 && (
              <div style={{ marginTop: 6, textAlign: "center" }}>
                <span style={{ fontSize: 8, color: "#818cf8", fontWeight: 700 }}>✦ AI fixing all issues...</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Shopping */}
      <div style={{ flex: step >= 10 ? 1 : 0, padding: step >= 10 ? "4px 12px" : "0 12px", opacity: step >= 10 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden" }}>
        <ShoppingPanel revealed={step >= 11} ranked={step >= 12} badge={step >= 13}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "6px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "#fff", color: "#0f172a", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 20, boxShadow: "0 3px 12px rgba(0,0,0,0.3)" }}>
          Get My Free Audit
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VARIANT 15: The Gap — Your store vs top performers
   ══════════════════════════════════════════════════ */
function Variant15() {
  const [step, setStep] = useState(0);
  const [yourScore, setYourScore] = useState(0);
  const [topScore, setTopScore] = useState(0);
  // 0=init, 1=your score counting, 2=top score counting, 3=gap reveal, 4=reasons, 5=title, 6=desc, 7=hl, 8=fixing, 9=closing gap, 10=shopping, 11=reveal, 12=rank, 13=badge
  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setYourScore(0); setTopScore(0); await w(600);
        setStep(1);
        for (let i = 0; i <= 32; i++) { if (!running) return; setYourScore(i); await w(40); }
        await w(500);
        setStep(2);
        for (let i = 0; i <= 96; i++) { if (!running) return; setTopScore(i); await w(20); }
        await w(800);
        setStep(3); await w(1200);
        setStep(4); await w(400);
        setStep(5); await w(800);
        setStep(6); await w(800);
        setStep(7); await w(800);
        setStep(8); await w(1200);
        setStep(9);
        for (let i = 32; i <= 98; i++) { if (!running) return; setYourScore(i); await w(25); }
        await w(1000);
        setStep(10); await w(800);
        setStep(11); await w(800);
        setStep(12); await w(800);
        setStep(13); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  const gap = topScore - yourScore;
  const yourColor = yourScore < 50 ? "#EF4444" : yourScore < 80 ? "#F59E0B" : "#10B981";

  return (
    <div style={{ width: "100%", height: "100%", background: "#fff", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#4B6AE8", textTransform: "uppercase", letterSpacing: 1 }}>Gap Analysis</div>
      </div>

      {/* Scores comparison */}
      <div style={{ flex: step < 10 ? 1 : 0, padding: "0 16px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "flex 0.8s ease" }}>
        <div style={{ display: "flex", gap: 20, alignItems: "flex-end", marginBottom: 12 }}>
          {/* Your store */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", marginBottom: 4 }}>Your Store</div>
            <div style={{ width: 80, height: 80, borderRadius: "50%", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width={80} height={80} viewBox="0 0 80 80" style={{ position: "absolute" }}>
                <circle cx={40} cy={40} r={35} fill="none" stroke="#f1f5f9" strokeWidth={4}/>
                <circle cx={40} cy={40} r={35} fill="none" stroke={yourColor} strokeWidth={4}
                  strokeDasharray={`${(yourScore / 100) * 219.91} 219.91`} strokeLinecap="round"
                  style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%", transition: "stroke-dasharray 0.05s linear, stroke 0.3s ease" }}/>
              </svg>
              <span style={{ fontSize: 22, fontWeight: 900, color: yourColor, transition: "color 0.3s ease" }}>{yourScore}</span>
            </div>
          </div>

          {/* Gap indicator */}
          {step >= 3 && gap > 5 && (
            <div style={{ textAlign: "center", animation: "popIn 0.4s ease", marginBottom: 20 }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: "#EF4444" }}>-{gap}</div>
              <div style={{ fontSize: 8, color: "#dc2626", fontWeight: 600 }}>GAP</div>
            </div>
          )}
          {step >= 9 && gap <= 5 && (
            <div style={{ textAlign: "center", animation: "popIn 0.4s ease", marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: "#10B981" }}>≈</div>
              <div style={{ fontSize: 8, color: "#059669", fontWeight: 600 }}>MATCH!</div>
            </div>
          )}

          {/* Top performers */}
          <div style={{ textAlign: "center", opacity: step >= 2 ? 1 : 0.2, transition: "opacity 0.5s ease" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", marginBottom: 4 }}>Top 10%</div>
            <div style={{ width: 80, height: 80, borderRadius: "50%", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width={80} height={80} viewBox="0 0 80 80" style={{ position: "absolute" }}>
                <circle cx={40} cy={40} r={35} fill="none" stroke="#f1f5f9" strokeWidth={4}/>
                <circle cx={40} cy={40} r={35} fill="none" stroke="#10B981" strokeWidth={4}
                  strokeDasharray={`${(topScore / 100) * 219.91} 219.91`} strokeLinecap="round"
                  style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%", transition: "stroke-dasharray 0.05s linear" }}/>
              </svg>
              <span style={{ fontSize: 22, fontWeight: 900, color: "#10B981" }}>{topScore}</span>
            </div>
          </div>
        </div>

        {/* Audit rows */}
        {step >= 4 && step < 10 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
            <AuditRow icon="📝" label="Title Quality" active={step >= 5} status="fail" detail="12 chars vs 60+ avg"/>
            <AuditRow icon="📄" label="Description" active={step >= 6} status="fail" detail="120 chars vs 2000+ avg"/>
            <AuditRow icon="⭐" label="Highlights" active={step >= 7} status="fail" detail="0/5 vs 5/5 avg"/>
          </div>
        )}

        {step >= 8 && step < 10 && (
          <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, background: "#eef2ff", borderRadius: 8, padding: "6px 14px", animation: "popIn 0.4s ease" }}>
            <div style={{ width: 10, height: 10, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#4B6AE8" }}>Closing the gap with AI...</span>
          </div>
        )}
      </div>

      {/* Shopping */}
      <div style={{ flex: step >= 10 ? 1 : 0, padding: step >= 10 ? "4px 12px" : "0 12px", opacity: step >= 10 ? 1 : 0, transition: "all 0.8s ease", overflow: "hidden" }}>
        <ShoppingPanel revealed={step >= 11} ranked={step >= 12} badge={step >= 13}/>
      </div>

      {/* CTA */}
      <div style={{ padding: "6px 16px 12px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "8px 20px", borderRadius: 8, boxShadow: "0 3px 12px rgba(75,106,232,0.35)" }}>
          Close My Gap — Free Audit
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VIDEO DOWNLOAD BUTTON (reused from AdsCreatives)
   ══════════════════════════════════════════════════ */

const VIDEO_SIZES = {
  "1:1": { w: 540, h: 540, label: "1:1" },
  "16:9": { w: 720, h: 405, label: "16:9" },
  "9:16": { w: 405, h: 720, label: "9:16" },
};

const KEYFRAMES_CSS = `
  @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
  @keyframes pulseMF { 0%,100%{opacity:1} 50%{opacity:0.3} }
  @keyframes popIn { 0%{opacity:0;transform:scale(0.75)} 100%{opacity:1;transform:scale(1)} }
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
`;

function DownloadButton({ name, Component }) {
  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentFormat, setCurrentFormat] = useState("");

  const startRecording = useCallback(async (formatKey) => {
    const vf = VIDEO_SIZES[formatKey];
    setRecording(true); setProgress(0); setCurrentFormat(formatKey);

    const container = document.createElement("div");
    container.style.cssText = `position:fixed;top:-9999px;left:-9999px;width:${vf.w}px;height:${vf.h}px;overflow:hidden;z-index:-1;pointer-events:none;`;
    document.body.appendChild(container);

    const style = document.createElement("style");
    style.textContent = KEYFRAMES_CSS;
    container.appendChild(style);

    const wrapper = document.createElement("div");
    wrapper.style.cssText = `width:${vf.w}px;height:${vf.h}px;font-family:'Inter',sans-serif;`;
    container.appendChild(wrapper);

    const reactRoot = ReactDOM.createRoot(wrapper);
    reactRoot.render(<Component />);

    await new Promise(r => setTimeout(r, 2000));

    const canvas = document.createElement("canvas");
    canvas.width = vf.w; canvas.height = vf.h;
    const ctx = canvas.getContext("2d");
    const stream = canvas.captureStream(30);

    let mimeType = "video/webm;codecs=vp9";
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "video/webm";
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5000000 });
    const chunks = [];
    recorder.ondataavailable = e => e.data.size > 0 && chunks.push(e.data);
    recorder.start(100);

    const duration = 17000;
    const start = Date.now();
    while (Date.now() - start < duration) {
      try {
        const c = await html2canvas(wrapper, { width: vf.w, height: vf.h, scale: 1, useCORS: true, logging: false });
        ctx.drawImage(c, 0, 0);
      } catch (e) {}
      setProgress(Math.min(100, Math.round(((Date.now() - start) / duration) * 100)));
      await new Promise(r => setTimeout(r, 30));
    }

    recorder.stop();
    await new Promise(r => { recorder.onstop = r; });

    const blob = new Blob(chunks, { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `MFP-FB-${name.replace(/[^a-zA-Z0-9]/g, "-")}-${formatKey.replace(":", "x")}.webm`;
    a.click();
    URL.revokeObjectURL(url);

    reactRoot.unmount();
    document.body.removeChild(container);
    setRecording(false); setProgress(0); setCurrentFormat("");
  }, [name, Component]);

  if (recording) {
    return (
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 12, height: 12, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: "#4B6AE8", fontWeight: 600, marginBottom: 3 }}>Recording {currentFormat}... {progress}%</div>
          <div style={{ width: "100%", height: 3, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: "linear-gradient(90deg, #4B6AE8, #818cf8)", borderRadius: 2, transition: "width 0.3s ease" }}/>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
      {Object.entries(VIDEO_SIZES).map(([key, vf]) => (
        <button key={key} onClick={() => startRecording(key)} style={{
          background: "none", border: "1px solid #e5e7eb", borderRadius: 6,
          padding: "5px 10px", fontSize: 10, color: "#6b7280", cursor: "pointer",
          fontFamily: "'Inter',sans-serif", fontWeight: 600, display: "flex", alignItems: "center", gap: 4,
        }}>
          <span>🎬</span> {vf.label}
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN EXPORT
   ══════════════════════════════════════════════════ */

const FB_ADS = [
  { name: "Classic Audit Flow", desc: "Audit scan → score → Shopping #1", Component: Variant1 },
  { name: "Dark Mode Score", desc: "Dark theme, gauge counter, audit → rank", Component: Variant2 },
  { name: "Before / After Split", desc: "Before audit (red) → After with Shopping", Component: Variant3 },
  { name: "Terminal Hacker", desc: "Console-style audit commands → Shopping", Component: Variant4 },
  { name: "Score Journey", desc: "Big counter + progress bars → Shopping #1", Component: Variant5 },
  { name: "Money Leak", desc: "Revenue draining from poor feed → AI stops the leak", Component: Variant6 },
  { name: "Invisible Products", desc: "Your products can't be found → AI makes them visible", Component: Variant7 },
  { name: "Competitor Spy", desc: "They outrank you — bar comparison → overtake them", Component: Variant8 },
  { name: "Disapproval Alert", desc: "Merchant Center rejections → all approved", Component: Variant9 },
  { name: "Wasted Ad Spend", desc: "67% budget wasted → 3.2x better ROAS", Component: Variant10 },
  { name: "Time Bomb", desc: "Every day = lost sales counter → fix now", Component: Variant11 },
  { name: "X-Ray Scanner", desc: "Deep product scan with laser line → diagnosis", Component: Variant12 },
  { name: "Social Proof", desc: "2,847 merchants fixed their feed → your turn", Component: Variant13 },
  { name: "Phone Notifications", desc: "Push alerts stacking up → audit reveals why", Component: Variant14 },
  { name: "The Gap", desc: "Your score vs top 10% — close the gap with AI", Component: Variant15 },
];

export default function FacebookAds() {
  return (
    <div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
        gap: 24,
      }}>
        {FB_ADS.map((ad, i) => (
          <div key={i}>
            <div style={{
              width: "100%", maxWidth: 420,
              aspectRatio: "1/1",
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}>
              <ad.Component />
            </div>
            <div style={{ marginTop: 10, display: "flex", alignItems: "baseline", gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#E1306C" }}>{String(i + 1).padStart(2, "0")}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{ad.name}</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>{ad.desc}</div>
              </div>
            </div>
            <DownloadButton name={ad.name} Component={ad.Component}/>
          </div>
        ))}
      </div>
    </div>
  );
}
